export type DetectionModel = 'lite' | 'mobilenet' | 'none';

export interface AISettings {
  customPrompt: string;
  multipleItemsPerPhoto: boolean;
  detectionModel: DetectionModel;
  detectionConfidence: number; // 0-100 (minimum confidence threshold)
  maxDetections: number; // Maximum number of objects to detect
  showRealtimeBoxes: boolean;
}

export const getAISettings = (): AISettings => {
  const savedSettings = localStorage.getItem('aiSettings');
  
  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      // Migrate old detectionMethod to detectionModel
      if (parsed.detectionMethod) {
        if (parsed.detectionMethod === 'none') {
          parsed.detectionModel = 'none';
        } else {
          parsed.detectionModel = 'lite'; // Default browser methods to lite
        }
        delete parsed.detectionMethod;
      }
      return { ...getDefaultSettings(), ...parsed };
    } catch (error) {
      console.error('Failed to parse AI settings:', error);
    }
  }
  
  return getDefaultSettings();
};

const getDefaultSettings = (): AISettings => {
  return {
    customPrompt: '',
    multipleItemsPerPhoto: false,
    detectionModel: 'lite', // Default to lite (fastest)
    detectionConfidence: 30, // Lower default for better detection (30%)
    maxDetections: 50, // Detect up to 50 objects (increased from 20)
    showRealtimeBoxes: true
  };
};

export const saveAISettings = (settings: AISettings) => {
  try {
    localStorage.setItem('aiSettings', JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save AI settings to localStorage:', error);
  }
};

// Migrate localStorage settings to API (call once on login)
export const migrateSettingsToAPI = async (usersApi: any): Promise<boolean> => {
  try {
    const localSettings = localStorage.getItem('aiSettings');
    if (!localSettings) {
      return false; // No local settings to migrate
    }

    const parsed = JSON.parse(localSettings);
    
    // Save to API
    await usersApi.updateSettings(parsed);
    
    // Clear localStorage after successful migration
    localStorage.removeItem('aiSettings');
    
    console.log('✅ Settings migrated from localStorage to database');
    return true;
  } catch (error) {
    console.error('Failed to migrate settings:', error);
    return false;
  }
};

// Load settings from API and cache in localStorage for offline access
export const loadSettingsFromAPI = async (usersApi: any): Promise<AISettings> => {
  try {
    const response = await usersApi.getSettings();
    if (response.data.success && response.data.data) {
      const settings = response.data.data;
      // Cache in localStorage for offline access
      localStorage.setItem('aiSettings', JSON.stringify(settings));
      return settings;
    }
  } catch (error) {
    console.warn('Failed to load settings from API, using local cache:', error);
  }
  
  // Fallback to localStorage if API fails
  return getAISettings();
};

// Save settings to API and localStorage
export const saveSettingsToAPI = async (usersApi: any, settings: AISettings): Promise<boolean> => {
  try {
    await usersApi.updateSettings(settings);
    // Also save to localStorage for offline access
    saveAISettings(settings);
    console.log('✅ Settings saved to database');
    return true;
  } catch (error) {
    console.error('Failed to save settings to API:', error);
    // Still save to localStorage as fallback
    saveAISettings(settings);
    return false;
  }
};

export const buildAIPrompt = (basePrompt: string): string => {
  const settings = getAISettings();
  
  if (settings.customPrompt) {
    return `${settings.customPrompt}\n\n${basePrompt}`;
  }
  
  return basePrompt;
};

